import { Calendar, Eye, Shirt, Trash2, Image } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// ── Inventario ────────────────────────────────────────────────
export const getColumnsInventario = (onDelete) => [
  {
    id: 'tipoPrenda',
    label: 'Tipo de Prenda',
    render: (row) => (
      <div className="flex items-center gap-2">
        <Shirt className="h-4 w-4 text-purple-600" />
        <span className="font-medium">{row.tipoPrenda?.nombre || 'N/A'}</span>
      </div>
    ),
  },
  {
    id: 'cantidad',
    label: 'Cantidad',
    render: (row) => (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
        {row.cantidad || 0} unidades
      </Badge>
    ),
  },
  {
    id: 'peso',
    label: 'Peso',
    render: (row) => (
      <span className="text-sm text-gray-600">{row.peso ? `${row.peso} kg` : 'N/A'}</span>
    ),
  },
  {
    id: 'descripcion',
    label: 'Descripción',
    render: (row) => (
      <span className="text-sm text-gray-600">{row.descripcion || '-'}</span>
    ),
  },
  {
    id: 'estado',
    label: 'Estado',
    render: (row) => (
      <Badge
        variant="outline"
        className={
          row.estado === 'Activo'
            ? 'bg-green-50 text-green-700 border-green-200'
            : 'bg-gray-50 text-gray-700 border-gray-200'
        }
      >
        {row.estado}
      </Badge>
    ),
  },
  {
    id: 'actions',
    label: 'Acciones',
    render: (row) => (
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={() => onDelete(row._id)}>
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      </div>
    ),
  },
];

// ── Movimientos ───────────────────────────────────────────────
export const columnsMovimientos = [
  {
    id: 'fecha',
    label: 'Fecha',
    render: (row) => (
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-400" />
        <span className="text-sm">
          {new Date(row.fecha).toLocaleDateString('es-ES')}{' '}
          {new Date(row.fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    ),
  },
  {
    id: 'tipo',
    label: 'Tipo',
    render: (row) => {
      const colors = {
        Entrega: 'bg-green-50 text-green-700',
        Recoleccion: 'bg-orange-50 text-orange-700',
        Ajuste: 'bg-blue-50 text-blue-700',
        Devolucion: 'bg-red-50 text-red-700',
      };
      return (
        <Badge variant="outline" className={colors[row.tipo] || 'bg-gray-50 text-gray-700'}>
          {row.tipo}
        </Badge>
      );
    },
  },
  {
    id: 'tipoPrenda',
    label: 'Prenda',
    render: (row) => <span className="text-sm font-medium">{row.tipoPrenda?.nombre || '-'}</span>,
  },
  {
    id: 'cantidad',
    label: 'Cantidad',
    render: (row) => <span className="font-semibold">{row.cantidad}</span>,
  },
  {
    id: 'referencia',
    label: 'Referencia',
    render: (row) => (
      <span className="text-sm text-gray-600">{row.referenciaConsecutivo || '-'}</span>
    ),
  },
  {
    id: 'observaciones',
    label: 'Observaciones',
    render: (row) => (
      <span className="text-sm text-gray-500 truncate max-w-[150px] block">
        {row.observaciones || '-'}
      </span>
    ),
  },
];

// ── Entregas ──────────────────────────────────────────────────
export const getColumnsEntregas = (onViewTicket) => [
  {
    id: 'consecutivo',
    label: 'Consecutivo',
    render: (row) => (
      <span className="font-semibold text-green-600">{row.consecutivo}</span>
    ),
  },
  {
    id: 'fecha',
    label: 'Fecha',
    render: (row) => (
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-400" />
        <span className="text-sm">
          {new Date(row.fecha_entrega || row.fecha_creacion).toLocaleDateString('es-ES')}
        </span>
      </div>
    ),
  },
  {
    id: 'estado',
    label: 'Estado',
    render: (row) => {
      const colors = {
        'Alistado': 'bg-blue-50 text-blue-700',
        'En Camino': 'bg-yellow-50 text-yellow-700',
        'Entregado': 'bg-green-50 text-green-700',
        'Cancelado': 'bg-red-50 text-red-700',
      };
      return (
        <Badge variant="outline" className={colors[row.estado] || 'bg-gray-50 text-gray-700'}>
          {row.estado}
        </Badge>
      );
    },
  },
  {
    id: 'prendas',
    label: 'Prendas',
    render: (row) => {
      const total = row.items?.reduce((s, i) => s + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0) || 0;
      return <span className="font-medium">{total}</span>;
    },
  },
  {
    id: 'conductor',
    label: 'Conductor',
    render: (row) => <span className="text-sm text-gray-600">{row.conductor?.nombre || '-'}</span>,
  },
  {
    id: 'ticket',
    label: '',
    render: (row) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onViewTicket(row)}
        className="gap-1.5 text-green-700 hover:text-green-900 hover:bg-green-50"
      >
        <Eye className="h-4 w-4" /> Ver ticket
      </Button>
    ),
  },
];

// ── Recolecciones ─────────────────────────────────────────────
export const getColumnsRecolecciones = (onViewTicket) => [
  {
    id: 'consecutivo',
    label: 'Consecutivo',
    render: (row) => (
      <div className="flex items-center gap-1.5">
        <span className="font-semibold text-orange-600">{row.consecutivo}</span>
        {Array.isArray(row.evidencias) && row.evidencias.length > 0 && (
          <span title={`${row.evidencias.length} evidencia(s)`} className="inline-flex items-center gap-0.5 bg-purple-100 text-purple-700 border border-purple-200 rounded-full px-1.5 py-0.5 text-[10px] font-bold">
            <Image className="h-3 w-3" />
            {row.evidencias.length}
          </span>
        )}
      </div>
    ),
  },
  {
    id: 'fecha',
    label: 'Fecha',
    render: (row) => (
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-400" />
        <span className="text-sm">
          {new Date(row.fecha_recoleccion || row.fecha_creacion).toLocaleDateString('es-ES')}
        </span>
      </div>
    ),
  },
  {
    id: 'estado',
    label: 'Estado',
    render: (row) => {
      const colors = {
        'Entregado': 'bg-orange-50 text-orange-700',
        'Pendiente Verificacion': 'bg-yellow-50 text-yellow-700',
        'Verificado': 'bg-green-50 text-green-700',
        'Cancelado': 'bg-red-50 text-red-700',
      };
      return (
        <Badge variant="outline" className={colors[row.estado] || 'bg-gray-50 text-gray-700'}>
          {row.estado}
        </Badge>
      );
    },
  },
  {
    id: 'prendas',
    label: 'Prendas',
    render: (row) => {
      const total = row.items?.reduce((s, i) => s + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0) || 0;
      return <span className="font-medium">{total}</span>;
    },
  },
  {
    id: 'conductor',
    label: 'Conductor',
    render: (row) => <span className="text-sm text-gray-600">{row.conductor?.nombre || '-'}</span>,
  },
  {
    id: 'ticket',
    label: '',
    render: (row) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onViewTicket(row)}
        className="gap-1.5 text-orange-700 hover:text-orange-900 hover:bg-orange-50"
      >
        <Eye className="h-4 w-4" /> Ver ticket
      </Button>
    ),
  },
];

// ── Alistamientos ─────────────────────────────────────────────
export const columnsAlistamientos = [
  {
    id: 'consecutivo',
    label: 'Consecutivo',
    render: (row) => (
      <span className="font-semibold text-indigo-600">{row.consecutivo}</span>
    ),
  },
  {
    id: 'fecha',
    label: 'Fecha',
    render: (row) => (
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-400" />
        <span className="text-sm">{new Date(row.fechaCreacion).toLocaleDateString('es-ES')}</span>
      </div>
    ),
  },
  {
    id: 'estado',
    label: 'Estado',
    render: (row) => {
      const colors = {
        'Pendiente': 'bg-yellow-50 text-yellow-700',
        'Alistado': 'bg-purple-50 text-purple-700',
      };
      return (
        <Badge variant="outline" className={colors[row.estado] || 'bg-gray-50 text-gray-700'}>
          {row.estado}
        </Badge>
      );
    },
  },
  {
    id: 'zona',
    label: 'Zona',
    render: (row) => <span className="text-sm">{row.zona || '-'}</span>,
  },
  {
    id: 'totalItems',
    label: 'Total Items',
    render: (row) => <span className="font-medium">{row.totalItems || 0}</span>,
  },
  {
    id: 'bolsas',
    label: 'Bolsas',
    render: (row) => <span className="text-sm">{row.bolsas?.length || 0}</span>,
  },
];
